/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/import-whitelist
import {
	PROJECT_TYPES,
	type ProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { AnyAri } from '@atlassian/ari/any-ari';
import { JiraProjectCategoryAri } from '@atlassian/ari/jira/project-category';
// eslint-disable-next-line jira/import-whitelist
import { matchAliases } from '@jira/global-pages__directories-directory-base-v3/src/utils/search-string/index.tsx';
import type { Query } from '@atlassian/react-resource-router';
import type { JiraProjectFilterInput } from '@jira/global-pages__directories-projects-directory-v3/src/ui/page-layout/__generated__/ProjectDirectoryRefetchQuery.graphql';
import {
	type TableColumns,
	SortableColumns,
	FilterFields,
	filterInitialState,
} from './filterFields.constants.tsx';

type Dictionary = {
	[key: string]: string;
};

const LEGACY_ALL_VALUE = 'all';

export const sanitizeLegacyQuery = (
	query: Query,
): {
	selectedProjectType: string;
	selectedCategory: string;
} => ({
	...query,
	selectedProjectType:
		query.selectedProjectType === LEGACY_ALL_VALUE
			? filterInitialState[FilterFields.ProjectType]
			: query.selectedProjectType,
	selectedCategory:
		query.selectedCategory === LEGACY_ALL_VALUE
			? filterInitialState[FilterFields.ProjectCategoryId]
			: query.selectedCategory,
});

export const mapQueryToFilter = <T extends Dictionary>(
	filter: T,
	{
		cloudId,
		query,
		aliases,
	}: { cloudId: string; query?: Query; aliases?: { [alias: string]: keyof T } },
): JiraProjectFilterInput => {
	const variables: JiraProjectFilterInput = {};
	const {
		[FilterFields.Keyword]: keyword,
		[FilterFields.ProjectCategoryId]: projectCategoryId,
		[FilterFields.ProjectType]: types,
		[FilterFields.SortOrder]: sortOrder,
		[FilterFields.SortKey]: sortKey,
	} = aliases && query ? matchAliases(filter, query, aliases) : filter;

	if (keyword) {
		variables.keyword = keyword;
	}

	if (projectCategoryId) {
		const isAlreadyAri = AnyAri.check(projectCategoryId);
		if (isAlreadyAri) {
			variables.projectCategoryId = projectCategoryId;
		} else {
			variables.projectCategoryId = JiraProjectCategoryAri.create({
				projectCategoryId,
				siteId: cloudId,
			}).toString();
		}
	}

	if (sortKey || sortOrder) {
		const sortByValue =
			// oxlint-disable-next-line typescript/consistent-type-assertions
			sortKey && SortableColumns.includes(sortKey as TableColumns)
				? sortKey
				: filterInitialState[FilterFields.SortKey];
		// oxlint-disable-next-line typescript/consistent-type-assertions
		variables.sortBy = {
			order: ['ASC', 'DESC'].includes(sortOrder)
				? sortOrder
				: filterInitialState[FilterFields.SortOrder],
			sortBy: sortByValue.toUpperCase(),
		} as JiraProjectFilterInput['sortBy'];
	}

	if (types && typeof types === 'string') {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		variables.types = types
			.split(',') // oxlint-disable-next-line typescript/consistent-type-assertions
			.filter((type) => PROJECT_TYPES.includes(type as ProjectType))
			.map((type) => type.toUpperCase()) as unknown as JiraProjectFilterInput['types'];
	}
	return variables;
};
